<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <form @submit.prevent="submit">
        <div class="row">
          <div class="col-xs-6">
            <help-text>
              <h3>
                Select Content
              </h3>

              <p>
                First, select the ads and subscription periods to include in the order confirmation.
                These will be included in the order confirmation.
              </p>

              <p>
                Also, the selected ads will form the base for the information about the payer of the
                order confirmation. If no payer is selected for the order, the company itself will be
                filled out as the payer.
              </p>

              <p>
                The order title and number will also be fetched from the selected ads.
              </p>
            </help-text>
          </div>

          <div class="col-xs-18">
            <div class="panel panel-default">
              <div class="panel-body">
                <buyable-select
                    v-model="buyables"
                    :orders="orders.data"
                    :subscriptions="subscriptions.data"
                    :disable-invoiced-ads="false"
                    :disable-unpublished-ads="false"
                    headline="Subscription / Order"
                ></buyable-select>
              </div>
            </div>
          </div>
        </div>

        <hr class="panel-separator">

        <div class="row">
          <div class="col-xs-6">
            <help-text>
              <h3>
                Edit Information
              </h3>

              <p>
                In these panels, you may override the data in the order confirmation. The default data
                is pulled from the ads that are included in the confirmation.
              </p>
            </help-text>
          </div>

          <div class="col-xs-18">
            <additional-information-panel
                v-model="orderConfirmation"
                :legal-texts="legalTexts"
            ></additional-information-panel>

            <heading-information-panel
                v-model="orderConfirmation"
            />

            <payer-information-panel v-model="orderConfirmation"></payer-information-panel>

            <advertiser-information-panel v-model="orderConfirmation"></advertiser-information-panel>
          </div>
        </div>

        <hr class="panel-separator">

        <div class="row">
          <div class="col-xs-offset-6 col-xs-18">
            <button type="submit" class="btn btn-black pull-left" style="margin-bottom: 22px">
              <i class="fa fa-save"></i> Save
            </button>

            <button
                type="button"
                class="btn btn-white pull-right"
                style="border: 1px solid #000000;"
                @click="preview"
            >
              <i class="fa fa-cloud-download-alt"></i> Preview
            </button>

            <div class="clearfix"></div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import download from 'downloadjs'
import http from '@/http'
import moment from 'moment'
import notify from '@/notify'
import store from '@/store'
import AdditionalInformationPanel from '@/pages/Companies/Companies/Edit/Tabs/CreateOrderConfirmation/Panels/AdditionalInformation'
import AdvertiserInformationPanel from '@/pages/Companies/Companies/Edit/Tabs/CreateOrderConfirmation/Panels/AdvertiserInformation'
import BuyableSelect from '@/components/BuyableSelect'
import Errors from '@/mixins/Errors'
import HeadingInformationPanel from '@/pages/Companies/Companies/Edit/Tabs/CreateOrderConfirmation/Panels/HeadingInformation'
import HelpText from '@/components/HelpText'
import Load from '@/mixins/Load'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import PayerInformationPanel from '@/pages/Companies/Companies/Edit/Tabs/CreateOrderConfirmation/Panels/PayerInformation'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'

export default {
  components: {
    AdditionalInformationPanel,
    AdvertiserInformationPanel,
    BuyableSelect,
    HeadingInformationPanel,
    HelpText,
    LoadingContainer,
    PayerInformationPanel,
    SubmitFormGroup,
  },

  mixins: [Errors, Load],

  model: {
    prop: 'company',
    event: 'company-updated'
  },

  props: ['company'],

  data() {
    return {
      shared: store,
      orderConfirmation: {
        ean_number: this.company.data.ean,
        company_name: this.company.data.name,
        company_address: this.company.data.address.street_address,
        company_zip_code: this.company.data.address.zip_code,
        company_city: this.company.data.address.city,
        company_country: this.company.data.address.verbose_country,
        company_cvr: this.company.data.cvr,
        date: moment().format('YYYY-MM-DD')
      },
      buyables: [],
      orders: null,
      subscriptions: null,
      legalTexts: null
    }
  },

  mounted() {
    this.load({
      legalTexts: '/api/v2/legal-texts',
      orders: '/api/v1/orders?company=' + this.company.data.id,
      subscriptions: '/api/v1/subscriptions?company=' + this.company.data.id
    }, () => {
      this.orders.data = this.orders.data.map(order => {
        order.ads.sort((a, b) => a.sort_key > b.sort_key ? 1 : -1)

        return order
      })

      if (this.legalTexts.data.length) {
        this.orderConfirmation.legal_text_id = this.legalTexts.data[0].id
      }
    })
  },

  watch: {
    buyables(value) {
      if (value.length === 0) {
        return this.resetData()
      }

      this.getDataFromBuyable(value[0])
    }
  },

  computed: {
    previewData() {
      return {...this.orderConfirmation, ...this.previewLines}
    },

    previewLines() {
      return this.buyables.reduce((result, buyable, index) => {
        return {
          ...result,
          ['lines[' + index + '][id]']: buyable.id,
          ['lines[' + index + '][type]']: buyable.type
        }
      }, {})
    },

    requestData() {
      return {...this.orderConfirmation, company_id: this.company.data.id, lines: this.buyables}
    }
  },

  methods: {
    submit() {
      http.post('/api/v1/order-confirmations', this.requestData)
          .then(() => {
            notify.success('The order confirmation was saved')

            this.$router.push('.')
          })
          .catch(error => {
            if (error.response.status === 400) {
              return notify.error(error.response.data.message)
            }

            throw error
          })
          .catch(this.catchValidationErrors)
    },

    preview() {
      axios
          .get('/api/v1/order-confirmation-preview', {
            params: this.previewData,
            headers: {
              "Authorization": "Bearer " + this.shared.token,
              "Accept": "application/pdf",
            },
            responseType: "blob"
          })
          .then(response => {
            let filename = response.headers["content-disposition"].slice(22, -1);

            download(response.data, filename);
          })
    },

    getDataFromBuyable(buyable) {
      if (buyable.type === 'time-based-ads' || buyable.type === 'publication-based-ads') {
        return this.getDataFromAd(buyable)
      }

      if (buyable.type === 'subscription-periods') {
        return this.getDataFromSubscriptionPeriod(buyable)
      }
    },

    getDataFromAd(ad) {
      let order = this.getOrderFromAd(ad)

      this.orderConfirmation = {
        ...this.orderConfirmation, ...this.getOrderHeading(order), ...this.getPayer(order)
      }
    },

    getOrderHeading(order) {
      return {
        order_title: order.title,
        order_number: 'HG' + order.id,
        purchase_order: order.purchase_order,
        company_contact: order.contact_person.name,
        user_id: order.user_id
      }
    },

    getPayer(buyable) {
      if (!buyable.other_payer) {
        return this.getDefaultPayer(buyable)
      }

      return {
        payer_name: buyable.other_payer.name,
        payer_address: buyable.other_payer.address.street_address,
        payer_zip_code: buyable.other_payer.address.zip_code,
        payer_city: buyable.other_payer.address.city,
        payer_country: buyable.other_payer.address.verbose_country,
        payer_cvr: buyable.other_payer.cvr,
        payer_contact: buyable.other_payer_contact_person ? buyable.other_payer_contact_person.name : ''
      }
    },

    getDefaultPayer(buyable) {
      return {
        payer_name: this.company.data.name,
        payer_address: this.company.data.address.street_address,
        payer_zip_code: this.company.data.address.zip_code,
        payer_city: this.company.data.address.city,
        payer_country: this.company.data.address.verbose_country,
        payer_cvr: this.company.data.cvr,
        payer_contact: buyable.contact_person ? buyable.contact_person.name : ''
      }
    },

    getOrderFromAd(ad) {
      return this.orders.data.find(order => {
        return order.ads.includes(ad)
      })
    },

    getDataFromSubscriptionPeriod(subscriptionPeriod) {
      let subscription = this.getSubscriptionFromSubscriptionPeriod(subscriptionPeriod)

      this.orderConfirmation = {
        ...this.orderConfirmation,
        ...this.getSubscriptionHeading(subscription),
        ...this.getPayer(subscription)
      }
    },

    getSubscriptionHeading(subscription) {
      return {
        order_title: 'Subscription #' + subscription.id,
        order_number: '',
        purchase_order: subscription.purchase_order,
        company_contact: subscription.contact_person ? subscription.contact_person.name : '',
        user_id: subscription.user_id
      }
    },

    getSubscriptionFromSubscriptionPeriod(subscriptionPeriod) {
      return this.subscriptions.data.find(subscription => {
        return subscription.subscription_periods.includes(subscriptionPeriod)
      })
    },

    resetData() {
      this.orderConfirmation = {
        ...this.orderConfirmation,
        ...{
          order_title: null,
          order_number: null,
          purchase_order: null,
          company_contact: null,
          user_id: null,
          payer_name: null,
          payer_address: null,
          payer_zip_code: null,
          payer_city: null,
          payer_country: null,
          payer_cvr: null,
          payer_contact: null
        }
      }
    }
  }
}
</script>
